<template>
  <div class="screenshots-area ptb-100">
    <div class="container">
      <div class="section-title text-center">
        <span class="sub-title">Interface do Sistema</span>
        <h2>Interface Moderna e Intuitiva</h2>
        <p>Gerencie todo o seu negócio com uma interface limpa, responsiva e fácil de usar</p>
      </div>

      <div class="screenshots-container">
        <!-- Tabs -->
        <div class="screenshot-tabs" data-aos="fade-up">
          <button
              v-for="screen in screenshots"
              :key="screen.id"
              :class="['tab-button', { active: activeTab === screen.id }]"
              @click="activeTab = screen.id"
          >
            <div class="tab-icon">
              <i :class="screen.icon"></i>
            </div>
            <div class="tab-content">
              <h4>{{ screen.title }}</h4>
              <p>{{ screen.subtitle }}</p>
            </div>
          </button>
        </div>

        <!-- Screenshot Display -->
        <div class="screenshot-display" data-aos="fade-up" data-aos-delay="100">
          <transition name="fade" mode="out-in">
            <div :key="activeTab" class="screenshot-wrapper">
              <div class="browser-mockup">
                <div class="browser-header">
                  <div class="browser-dots">
                    <span class="dot red"></span>
                    <span class="dot yellow"></span>
                    <span class="dot green"></span>
                  </div>
                  <div class="browser-url">
                    <i class="ph-lock"></i>
                    <span>app.hawkcrm.com.br/{{ getCurrentScreen.url }}</span>
                  </div>
                  <div class="browser-actions">
                    <i class="ph-minus"></i>
                    <i class="ph-squares-four"></i>
                  </div>
                </div>
                <div class="screenshot-image">
                  <img :src="getCurrentScreen.image" :alt="getCurrentScreen.title">
                </div>
              </div>

              <div class="screenshot-info">
                <div class="info-badge">
                  <i :class="getCurrentScreen.icon"></i>
                  {{ getCurrentScreen.category }}
                </div>
                <h3>{{ getCurrentScreen.title }}</h3>
                <p>{{ getCurrentScreen.description }}</p>
                <ul class="feature-highlights">
                  <li v-for="(feature, index) in getCurrentScreen.features" :key="index">
                    <i class="ph-check-circle"></i>
                    {{ feature }}
                  </li>
                </ul>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- CTA -->
      <div class="screenshots-cta text-center" data-aos="fade-up">
        <p class="cta-text">Veja o HawkCRM em ação</p>
        <a href="https://wa.me/5554992302401?text=Quero agendar uma demonstração do HawkCRM"
           target="_blank"
           class="btn-style-one green-color"
           rel="noopener noreferrer">
          Agendar Demonstração Gratuita
          <i class="ph-caret-right"></i>
        </a>
      </div>
    </div>

    <!-- Background Elements -->
    <div class="bg-elements">
      <div class="gradient-orb orb-1"></div>
      <div class="gradient-orb orb-2"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Screenshots',
  data() {
    return {
      activeTab: 1,
      screenshots: [
        {
          id: 1,
          icon: 'ph-funnel',
          title: 'Pipeline de Vendas',
          subtitle: 'Funil Visual',
          category: 'Vendas',
          url: 'leads/kanban',
          image: '../../assets/images/screenshots/screen1.png',
          description: 'Gerencie todo o seu funil de vendas com drag & drop. Visualize leads em diferentes estágios e acompanhe conversões em tempo real.',
          features: [
            'Arraste e solte entre estágios',
            'Filtros avançados e busca rápida',
            'Estatísticas de conversão por etapa',
            'Personalização completa do pipeline'
          ]
        },
        {
          id: 2,
          icon: 'ph-users-three',
          title: 'Gestão de Clientes',
          subtitle: 'CRM Completo',
          category: 'Clientes',
          url: 'customers/view/1',
          image: '../../assets/images/screenshots/screen2.jpg',
          description: 'Tenha uma visão 360° dos seus clientes. Histórico completo, contatos, projetos, faturas e tickets em um só lugar.',
          features: [
            'Perfil completo do cliente',
            'Timeline de interações',
            'Documentos e anexos',
            'Portal exclusivo para clientes'
          ]
        },
        {
          id: 3,
          icon: 'ph-chart-line',
          title: 'Relatórios Inteligentes',
          subtitle: 'Analytics',
          category: 'Relatórios',
          url: 'reports/dashboard',
          image: '../../assets/images/screenshots/screen3.jpg',
          description: 'Dashboards personalizáveis com métricas em tempo real. Tome decisões baseadas em dados concretos.',
          features: [
            'Gráficos interativos',
            'Filtros por período',
            'Exportação PDF/Excel',
            'Comparação de períodos'
          ]
        },
        {
          id: 4,
          icon: 'ph-file-text',
          title: 'Propostas Profissionais',
          subtitle: 'Documentos',
          category: 'Vendas',
          url: 'proposals/create',
          image: '../../assets/images/screenshots/screen4.jpg',
          description: 'Crie propostas comerciais lindas em minutos. Templates personalizáveis, assinatura digital e tracking de abertura.',
          features: [
            'Editor visual intuitivo',
            'Templates prontos',
            'Assinatura digital integrada',
            'Notificação quando aberto'
          ]
        },
        {
          id: 5,
          icon: 'ph-kanban',
          title: 'Gestão de Projetos',
          subtitle: 'Projetos',
          category: 'Projetos',
          url: 'projects/view/5',
          image: '../../assets/images/screenshots/screen1.png',
          description: 'Acompanhe projetos com Gantt Chart, controle de tempo e tarefas. Mantenha sua equipe alinhada e produtiva.',
          features: [
            'Gantt Chart interativo',
            'Timesheet integrado',
            'Milestones e entregas',
            'Faturamento por projeto'
          ]
        },
        {
          id: 6,
          icon: 'ph-receipt',
          title: 'Faturas e Pagamentos',
          subtitle: 'Financeiro',
          category: 'Financeiro',
          url: 'invoices/list',
          image: '../../assets/images/screenshots/screen2.jpg',
          description: 'Emita faturas profissionais, receba pagamentos online e automatize recorrências. Controle financeiro total.',
          features: [
            'Múltiplas moedas e impostos',
            'Pagamentos online integrados',
            'Faturas recorrentes automáticas',
            'Lembretes de vencimento'
          ]
        }
      ]
    }
  },
  computed: {
    getCurrentScreen() {
      return this.screenshots.find(s => s.id === this.activeTab) || this.screenshots[0]
    }
  }
}
</script>

<style scoped>
/* ===== AREA BASE ===== */
.screenshots-area {
  background: linear-gradient(180deg, #0a0a1a 0%, #16213e 100%);
  position: relative;
  overflow: hidden;
}

.section-title {
  position: relative;
  z-index: 2;
}

.section-title .sub-title,
.section-title h2,
.section-title p {
  color: white;
}

.section-title .sub-title {
  background: rgba(109, 93, 252, 0.2);
  border: 1px solid rgba(109, 93, 252, 0.5);
  color: #C5BFFF;
}

/* ===== CONTAINER ===== */
.screenshots-container {
  margin-top: 60px;
  position: relative;
  z-index: 2;
}

/* ===== TABS ===== */
.screenshot-tabs {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 50px;
}

.tab-button {
  background: rgba(255, 255, 255, 0.05);
  border: 2px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.tab-button:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(109, 93, 252, 0.5);
  transform: translateY(-3px);
}

.tab-button.active {
  background: linear-gradient(135deg, rgba(109, 93, 252, 0.2) 0%, rgba(67, 97, 238, 0.2) 100%);
  border-color: #6D5DFC;
  box-shadow: 0 4px 20px rgba(109, 93, 252, 0.3);
}

.tab-icon {
  width: 50px;
  height: 50px;
  background: rgba(109, 93, 252, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.tab-icon i {
  font-size: 26px;
  color: #C5BFFF;
}

.tab-button.active .tab-icon {
  background: linear-gradient(135deg, #6D5DFC 0%, #4361EE 100%);
}

.tab-button.active .tab-icon i {
  color: white;
}

.tab-content h4 {
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 5px;
  color: white;
}

.tab-content p {
  font-size: 13px;
  margin: 0;
  color: #b8b8b8;
}

/* ===== SCREENSHOT DISPLAY ===== */
.screenshot-display {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 40px;
  backdrop-filter: blur(10px);
}

.screenshot-wrapper {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 40px;
  align-items: center;
}

/* ===== BROWSER MOCKUP ===== */
.browser-mockup {
  background: #1a1a2e;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.browser-header {
  background: #0f0f1a;
  padding: 12px 15px;
  display: flex;
  align-items: center;
  gap: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.browser-dots {
  display: flex;
  gap: 6px;
}

.dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.dot.red { background: #ff5f56; }
.dot.yellow { background: #ffbd2e; }
.dot.green { background: #27c93f; }

.browser-url {
  flex: 1;
  background: rgba(255, 255, 255, 0.05);
  padding: 8px 15px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: #b8b8b8;
}

.browser-url i {
  color: #25D366;
  font-size: 14px;
}

.browser-actions {
  display: flex;
  gap: 10px;
  color: #666;
}

.browser-actions i {
  cursor: pointer;
  transition: color 0.3s ease;
}

.browser-actions i:hover {
  color: #C5BFFF;
}

.screenshot-image {
  background: #0a0a1a;
}

.screenshot-image img {
  width: 100%;
  display: block;
  min-height: 400px;
  object-fit: cover;
}

/* ===== SCREENSHOT INFO ===== */
.screenshot-info {
  color: white;
}

.info-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(109, 93, 252, 0.2);
  border: 1px solid rgba(109, 93, 252, 0.5);
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  color: #C5BFFF;
  margin-bottom: 20px;
}

.info-badge i {
  font-size: 16px;
}

.screenshot-info h3 {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 15px;
  color: white;
}

.screenshot-info p {
  font-size: 16px;
  line-height: 1.7;
  color: #b8b8b8;
  margin-bottom: 25px;
}

.feature-highlights {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 12px;
}

.feature-highlights li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 15px;
  color: #e0e0e0;
}

.feature-highlights i {
  font-size: 22px;
  color: #25D366;
  flex-shrink: 0;
  margin-top: 2px;
}

/* ===== CTA ===== */
.screenshots-cta {
  margin-top: 60px;
  position: relative;
  z-index: 2;
}

.cta-text {
  font-size: 18px;
  color: #C5BFFF;
  margin-bottom: 20px;
  font-weight: 600;
}

.btn-style-one {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 15px 35px;
  background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
  color: white;
  border-radius: 10px;
  font-weight: 600;
  font-size: 16px;
  text-decoration: none;
  transition: all 0.3s ease;
  border: 2px solid #25D366;
}

.btn-style-one:hover {
  background: linear-gradient(135deg, #128C7E 0%, #075E54 100%);
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(37, 211, 102, 0.5);
  color: white;
}

/* ===== BACKGROUND ===== */
.bg-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

.gradient-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(100px);
  animation: pulseOrb 10s ease-in-out infinite;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(109, 93, 252, 0.15) 0%, transparent 70%);
  top: -100px;
  right: -100px;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: radial-gradient(circle, rgba(67, 97, 238, 0.12) 0%, transparent 70%);
  bottom: -100px;
  left: -100px;
  animation-delay: 5s;
}

@keyframes pulseOrb {
  0%, 100% {
    transform: scale(1);
    opacity: 0.5;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.8;
  }
}

/* ===== ANIMATIONS ===== */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

/* ===== RESPONSIVE ===== */
@media (max-width: 991px) {
  .screenshot-tabs {
    grid-template-columns: repeat(2, 1fr);
  }

  .screenshot-wrapper {
    grid-template-columns: 1fr;
  }

  .screenshot-display {
    padding: 30px 20px;
  }

  .screenshot-info {
    text-align: center;
  }

  .feature-highlights {
    text-align: left;
  }
}

@media (max-width: 576px) {
  .screenshot-tabs {
    grid-template-columns: 1fr;
  }

  .tab-content h4 {
    font-size: 14px;
  }

  .screenshot-info h3 {
    font-size: 22px;
  }
}
</style>